/**
 * Import Monitor — ERP Balance tab.
 *
 * Full-page switch between the two ERP balance views: slots where Helix's
 * balance differs from the ERP's, and the live ERP shortage report.
 */
import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { CalendarX2, Scale } from 'lucide-react';
import ImportErpBalanceMismatchTab from './ImportErpBalanceMismatchTab';
import ImportErpShortageTab from './ImportErpShortageTab';

interface ImportErpBalanceTabProps {
  customerId: number;
}

type ErpBalanceView = 'mismatch' | 'shortage';

export default function ImportErpBalanceTab({ customerId }: ImportErpBalanceTabProps) {
  const [view, setView] = useState<ErpBalanceView>('mismatch');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <Tabs value={view} onValueChange={(v) => setView(v === 'shortage' ? 'shortage' : 'mismatch')}>
          <TabsList className="grid grid-cols-2 h-auto w-[380px] bg-gradient-to-r from-slate-50 to-slate-100">
            <TabsTrigger value="mismatch" className="py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-md">
              <div className="flex flex-col items-center gap-1">
                <Scale className={`h-5 w-5 ${view === 'mismatch' ? 'text-blue-600' : 'text-slate-500'}`} />
                <span className={`text-xs font-medium ${view === 'mismatch' ? 'text-blue-700' : 'text-slate-600'}`}>Balance Mismatch</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="shortage" className="py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-md">
              <div className="flex flex-col items-center gap-1">
                <CalendarX2 className={`h-5 w-5 ${view === 'shortage' ? 'text-red-600' : 'text-slate-500'}`} />
                <span className={`text-xs font-medium ${view === 'shortage' ? 'text-red-700' : 'text-slate-600'}`}>Shortage Report</span>
              </div>
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div>
          <div className="text-sm font-semibold">{view === 'shortage' ? 'ERP Shortage Report' : 'ERP Balance Mismatch'}</div>
          <div className="text-xs text-muted-foreground">
            {view === 'shortage'
              ? 'The ERP\'s overdue shortage, live from its latest complete push, with every line Helix needs to repair to match.'
              : 'Schedule slots where the Helix balance differs from the ERP balance.'}
          </div>
        </div>
      </div>

      {view === 'shortage'
        ? <ImportErpShortageTab customerId={customerId} />
        : <ImportErpBalanceMismatchTab customerId={customerId} />}
    </div>
  );
}
